import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const whatsappRef = useRef(null);

  useEffect(() => {
    const original = document.body.style.overflowX;
    document.body.style.overflowX = 'hidden';
    return () => {
      document.body.style.overflowX = original;
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('homepage');

  const languages = [
    { code: 'en', name: t('language-english') },
    { code: 'hi', name: t('language-hindi') },
    { code: 'mr', name: t('language-marathi') },
  ];

  const getAvailableLanguages = () => {
    if (location.pathname === '/emi-calculator') {
      return languages.filter(
        (lang) => lang.code === 'en' || lang.code === 'hi' || lang.code === 'mr'
      );
    }
    return languages;
  };

  const isApplicationPage = location.pathname.includes('/application');

  const navigationLinks = [
    { name: t('header-personal-loans'), path: '/' },
    { name: t('header-emi-calculator'), path: '/emi-calculator' },
    { name: t('header-lending-partners'), path: '/lending-partner' },
    { name: t('header-learn-credit'), path: 'https://snapfunds.in/how-to', external: true },
  ];

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(/Mobi|Android/i.test(navigator.userAgent));
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = '9662267474';
    const message = 'Hello! I would like to know more about your services.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const handleNavigationClick = (path) => {
    if (path.startsWith('http')) {
      window.location.href = path;
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
  };

  const handleLanguageChange = (e, langCode) => {
    e.preventDefault();
    localStorage.setItem('selectedLanguage', langCode);
    i18n.changeLanguage(langCode);
    setDropdownOpen(false);
  };

  return (
    <>
      <nav className="navbar navbar-expand-sm navbar-light custom-navbar sticky-top">
        <div className="container-fluid d-flex justify-content-between align-items-center flex-wrap">

          <a className="navbar-brand d-flex align-items-center" href="/">
            <img src="/images/snapfuns-logo.png" alt="Snap Funds" className="logo-img" />
          </a>

          {!isMobile && !isApplicationPage && (
            <div className="navbar-nav mx-auto">
              <div className="nav-links-container">
                {navigationLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.path}
                    className="nav-link nav-link-animated"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigationClick(link.path);
                    }}
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {isMobile && !isApplicationPage && (
            <button
              className="navbar-toggler"
              type="button"
              onClick={toggleMobileMenu}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
          )}

          {!isMobile && (
            <div
              className="language-dropdown"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button className="dropbtn">
                <img src="/images/globe-02.png" alt="Lang" />
                <img src="/images/Arrow.png" alt="Dropdown" />
              </button>
              {dropdownOpen && (
                <div className="header-dropdown-content">
                  {getAvailableLanguages().map((lang) => (
                    <a
                      key={lang.code}
                      href="#"
                      onClick={(e) => handleLanguageChange(e, lang.code)}
                      className={i18n.language === lang.code ? 'active' : ''}
                    >
                      {lang.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {isMobile && (
            <div className="language-dropdown-mobile position-relative">
              <button
                className="btn btn-light d-flex align-items-center dropbtn-mobile"
                onClick={toggleDropdown}
              >
                <img src="/images/globe-02.png" alt="Lang" />
                <img src="/images/Arrow.png" alt="Dropdown" />
              </button>
              {dropdownOpen && (
                <div className="header-dropdown-content-mobile">
                  {getAvailableLanguages().map((lang) => (
                    <a
                      key={lang.code}
                      href="#"
                      onClick={(e) => handleLanguageChange(e, lang.code)}
                      className={i18n.language === lang.code ? 'active' : ''}
                    >
                      {lang.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {isMobile && mobileMenuOpen && !isApplicationPage && (
          <div className="mobile-nav-menu">
            <div className="mobile-nav-links">
              {navigationLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.path}
                  className="mobile-nav-link mobile-nav-link-animated"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavigationClick(link.path);
                  }}
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {!isApplicationPage &&
        createPortal(
          <div
            ref={whatsappRef}
            className="whatsapp-fixed-button"
            onClick={handleWhatsAppClick}
            aria-label="Chat on WhatsApp"
            role="button"
          >
            <img
              src="/images/whatsapp-mobile-icon.png"
              alt="WhatsApp"
              className="whatsapp-fixed-icon"
              draggable={false}
            />
          </div>,
          document.body
        )}
    </>
  );
};

export default Navbar;
